import { LitElement, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { effect } from "@preact/signals-core";
import {
  customText,
  setCustomText,
  updatePreviewImage,
  config,
  isCustomTextValid,
} from "../store/customization-store";

/**
 * Custom Text Input Component
 *
 * Text input field with validation for product customization.
 * - Only allows alphabetic characters and spaces
 * - Auto-converts to uppercase
 * - Validates min/max length
 * - Debounced preview updates
 *
 * Usage:
 * <custom-text-input></custom-text-input>
 */
@customElement("custom-text-input")
export class CustomTextInput extends LitElement {
  @state() private _value = "";
  @state() private _minCharacters = 1;
  @state() private _maxCharacters = 20;
  @state() private _isValid = false;

  private _debounceTimer: number | null = null;

  constructor() {
    super();

    // Subscribe to store
    effect(() => {
      this._value = customText.value;
      this._minCharacters = config.value.minCharacters;
      this._maxCharacters = config.value.maxCharacters;
      this._isValid = isCustomTextValid.value;
    });
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this; // Light DOM for Tailwind
  }

  private _handleInput(e: Event) {
    const target = e.target as HTMLInputElement;
    const rawValue = target.value;

    // Sanitize in store (strips numbers, special chars; uppercases)
    setCustomText(rawValue);

    // Immediately correct the visible input value so invalid chars never flash
    const cursorPos = target.selectionStart ?? target.value.length;
    target.value = customText.value;
    // Restore cursor, accounting for any stripped characters
    const stripped = rawValue.length - customText.value.length;
    target.setSelectionRange(cursorPos - stripped, cursorPos - stripped);

    // Debounced preview update (400ms)
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
    }

    this._debounceTimer = window.setTimeout(() => {
      updatePreviewImage(customText.value);
    }, 400);
  }

  render() {
    return html`
      <div class="form__prints_input flex flex-col gap-3">
        <label class="form__label text-xs font-semibold uppercase tracking-widest">
          Please input your custom text
        </label>
        <input
          type="text"
          name="properties[Your Text]"
          .value=${this._value}
          @input=${this._handleInput}
          maxlength="${this._maxCharacters}"
          placeholder="Enter ${this._minCharacters}-${this._maxCharacters} characters"
          required
          class="w-full px-3 py-3 md:px-4 md:py-4 rounded-md bg-white border border-[var(--color-secondary-300)] text-sm font-medium text-[var(--color-neutral-950)] placeholder:text-[var(--color-neutral-500)] focus:outline-none focus:ring-2 focus:ring-[var(--color-secondary-300)] focus:border-[var(--color-secondary-500)] transition-colors"
        />
        ${!this._isValid && this._value.length > 0
          ? html`
              <p class="text-xs text-[var(--color-danger-600)] mt-1">
                Minimum ${this._minCharacters} character${this._minCharacters !== 1 ? "s" : ""} required
              </p>
            `
          : ""}
      </div>
    `;
  }
}
